import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import { useAuth } from '../../context/AuthContext';
import { 
  getDirecciones, 
  createDireccion, 
  updateDireccion, 
  deleteDireccion,
  setDireccionPrincipal,
  Direccion,
  DireccionFormData
} from '../../services/direcciones';
import { getRegiones, getComunasByRegion, Region, Comuna } from '../../services/regiones';

const AddressCard = styled(Card)({
  borderRadius: '15px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
  },
});

const AddAddressCard = styled(Card)({
  borderRadius: '15px',
  border: '2px dashed #2e5a33',
  backgroundColor: 'rgba(46, 90, 51, 0.02)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(46, 90, 51, 0.05)',
    borderColor: '#1a3d1e',
  },
});

// Estilos comunes para los TextFields
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: { xs: '12px', sm: '14px' },
    backgroundColor: '#ffffff',
    fontSize: { xs: '0.875rem', sm: '1rem' },
    border: '1.5px solid #e8e8e8',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      border: '1.5px solid #2e5a33',
      boxShadow: '0 2px 8px rgba(46, 90, 51, 0.1)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      border: '2px solid #2e5a33',
      boxShadow: '0 4px 12px rgba(46, 90, 51, 0.15)',
    },
    '& input::placeholder': {
      color: '#aaa',
      opacity: 1,
    },
    '& textarea::placeholder': {
      color: '#aaa',
      opacity: 1,
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.875rem', sm: '1rem' },
    fontWeight: 500,
    color: '#555',
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: '#2e5a33',
      fontWeight: 600,
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#fafbfc',
      padding: '0 8px',
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '8px',
    fontSize: '0.75rem',
    color: '#777',
  }
  
};

// Usar DireccionFormData de Django
const emptyDireccion: DireccionFormData = {
  apodo: '',
  calle: '',
  numero: '',
  comuna: '',
  ciudad: '',
  region: '',
  codigo_postal: '',
  referencia: '',
  telefono: '',
  nombre_contacto: '',
  es_principal: false,
};

const getAddressIcon = (apodo: string) => {
  const tipo = apodo.toLowerCase();
  if (tipo.includes('casa') || tipo.includes('hogar')) return <HomeIcon />;
  if (tipo.includes('trabajo') || tipo.includes('oficina')) return <WorkIcon />;
  switch (tipo) {
    case 'casa': return <HomeIcon />;
    case 'trabajo': return <WorkIcon />;
    default: return <LocationOnIcon />;
  }
};

const CustomerAddresses: React.FC = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Direccion[]>([]);
  const [regiones, setRegiones] = useState<Region[]>([]);
  const [comunas, setComunas] = useState<Comuna[]>([]);
  const [comunasFiltradas, setComunasFiltradas] = useState<Comuna[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState<Direccion | null>(null);
  const [formData, setFormData] = useState<DireccionFormData>(emptyDireccion);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Cargar regiones y comunas desde Django
  useEffect(() => {
    const fetchRegionesYComunas = async () => {
      try {
        console.log('📥 [REGIONES] Cargando desde Django...');
        
        // Cargar regiones
        const regionesData = await getRegiones();
        setRegiones(regionesData);
        console.log('✅ [REGIONES] Cargadas:', regionesData.length);

        // Cargar todas las comunas (ya están disponibles en el servicio)
        console.log('✅ [COMUNAS] Disponibles mediante getComunasByRegion');
      } catch (error) {
        console.error('❌ [REGIONES/COMUNAS] Error al cargar:', error);
      }
    };

    fetchRegionesYComunas();
  }, []);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('📥 [DIRECCIONES] Cargando desde Django...');
        const fetchedAddresses = await getDirecciones();
        
        console.log('✅ [DIRECCIONES] Cargadas:', fetchedAddresses.length);
        setAddresses(fetchedAddresses);
      } catch (error) {
        console.error('❌ [DIRECCIONES] Error al cargar:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar las direcciones',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [user]);

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData(emptyDireccion);
    setComunasFiltradas([]);
    setDialogOpen(true);
  };

  const handleEdit = async (address: Direccion) => {
    setEditingAddress(address);
    // Convertir Direccion a DireccionFormData
    const formDataFromAddress: DireccionFormData = {
      apodo: address.apodo,
      calle: address.calle,
      numero: address.numero,
      comuna: address.comuna,
      ciudad: address.ciudad,
      region: address.region,
      codigo_postal: address.codigo_postal,
      referencia: address.referencia,
      telefono: address.telefono,
      nombre_contacto: address.nombre_contacto,
      es_principal: address.es_principal,
    };
    setFormData(formDataFromAddress);
    
    // Cargar comunas según la región de la dirección
    if (address.region) {
      try {
        const regionSeleccionada = regiones.find(r => r.nombre === address.region);
        if (regionSeleccionada) {
          const comunasData = await getComunasByRegion(regionSeleccionada.id);
          setComunasFiltradas(comunasData);
        }
      } catch (error) {
        console.error('Error al cargar comunas:', error);
      }
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (addressId: number) => {
    if (!user) return;
    
    if (window.confirm('¿Estás seguro de que quieres eliminar esta dirección?')) {
      try {
        console.log('🗑️ [DIRECCIONES] Eliminando:', addressId);
        await deleteDireccion(addressId);
        setAddresses(prev => prev.filter(addr => addr.id !== addressId));
        console.log('✅ [DIRECCIONES] Eliminada exitosamente');
        setSnackbar({
          open: true,
          message: '✅ Dirección eliminada exitosamente',
          severity: 'success'
        });
      } catch (error) {
        console.error('❌ [DIRECCIONES] Error al eliminar:', error);
        setSnackbar({
          open: true,
          message: '❌ Error al eliminar la dirección',
          severity: 'error'
        });
      }
    }
  };

  const handleSetDefault = async (addressId: number) => {
    if (!user) return;

    try {
      console.log('⭐ [DIRECCIONES] Estableciendo predeterminada:', addressId);
      await setDireccionPrincipal(addressId);

      // Actualizar estado local
      setAddresses(prev => prev.map(addr => ({
        ...addr,
        es_principal: addr.id === addressId
      })));
      
      console.log('✅ [DIRECCIONES] Predeterminada actualizada');
      setSnackbar({
        open: true,
        message: '✅ Dirección predeterminada actualizada',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ [DIRECCIONES] Error al actualizar predeterminada:', error);
      setSnackbar({
        open: true,
        message: '❌ Error al actualizar la dirección predeterminada',
        severity: 'error'
      });
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // Validar campos requeridos
    if (!formData.apodo || !formData.calle || !formData.numero || !formData.comuna || !formData.region || !formData.telefono) {
      setSnackbar({
        open: true,
        message: '⚠️ Por favor completa todos los campos obligatorios',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('💾 [DIRECCIONES] Guardando...', formData);

      if (editingAddress) {
        // Editar dirección existente
        console.log('📝 [DIRECCIONES] Actualizando:', editingAddress.id);
        const updated = await updateDireccion(editingAddress.id, formData);

        setAddresses(prev => prev.map(addr => 
          addr.id === editingAddress.id ? updated : addr
        ));
        
        console.log('✅ [DIRECCIONES] Actualizada exitosamente');
        setSnackbar({
          open: true,
          message: '✅ Dirección actualizada exitosamente',
          severity: 'success'
        });
      } else {
        // Agregar nueva dirección
        console.log('➕ [DIRECCIONES] Agregando nueva dirección');
        const newAddress = await createDireccion(formData);
        
        setAddresses(prev => [...prev, newAddress]);
        
        console.log('✅ [DIRECCIONES] Agregada exitosamente, ID:', newAddress.id);
        setSnackbar({
          open: true,
          message: '✅ Nueva dirección agregada exitosamente',
          severity: 'success'
        });
      }

      setDialogOpen(false);
    } catch (error) {
      console.error('❌ [DIRECCIONES] Error al guardar:', error);
      setSnackbar({
        open: true,
        message: `❌ Error: ${error instanceof Error ? error.message : 'Error al guardar la dirección'}`,
        severity: 'error'
      });
    }
  };

  const handleChange = (field: keyof DireccionFormData) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Si se cambia la región, cargar las comunas correspondientes
    if (field === 'region') {
      const regionSeleccionada = regiones.find(r => r.nombre === value);
      if (regionSeleccionada) {
        try {
          const comunasData = await getComunasByRegion(regionSeleccionada.id);
          setComunasFiltradas(comunasData);
          // Limpiar la comuna seleccionada al cambiar de región
          setFormData(prev => ({ ...prev, region: value, comuna: '' }));
          return;
        } catch (error) {
          console.error('Error al cargar comunas:', error);
        }
      }
    }
    
    // Validación mejorada para teléfono con prefijo +56 automático
    if (field === 'telefono') {
      // Si está vacío o solo tiene +, establecer +56 9
      if (value === '' || value === '+') {
        value = '+56 9';
      }
      // Si no empieza con +56, agregarlo
      if (!value.startsWith('+56')) {
        value = '+56 9' + value.replace(/^\+?56\s?9?\s?/, '');
      }
      // Solo permitir números, espacios y el prefijo +56
      if (!/^\+56\s?9?[0-9\s]*$/.test(value)) {
        return; // No actualizar si no cumple el formato
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e5a33' }}>
          Mis Direcciones
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          sx={{
            background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
            },
          }}
        >
          Agregar Dirección
        </Button>
      </Box>

      <Grid container spacing={3}>
        {addresses.map((address) => (
          <Grid size={{ xs: 12, md: 6 }} key={address.id}>
            <AddressCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getAddressIcon(address.apodo || 'otro')}
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {address.apodo || 'Sin nombre'}
                    </Typography>
                    {address.es_principal && (
                      <Chip
                        icon={<StarIcon />}
                        label="Predeterminada"
                        size="small"
                        sx={{ bgcolor: '#2e5a33', color: 'white' }}
                      />
                    )}
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(address)}
                      sx={{ color: '#2e5a33' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(address.id)}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {address.calle} {address.numero}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {address.comuna}, {address.region}
                </Typography>
                
                {address.referencia && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mb: 2 }}>
                    "{address.referencia}"
                  </Typography>
                )}

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Teléfono: {address.telefono}
                </Typography>

                {address.nombre_contacto && (
                  <Typography variant="body2" color="textSecondary">
                    Contacto: {address.nombre_contacto}
                  </Typography>
                )}

                {!address.es_principal && (
                  <Button
                    size="small"
                    onClick={() => handleSetDefault(address.id)}
                    sx={{ mt: 2, color: '#2e5a33' }}
                  >
                    Establecer como predeterminada
                  </Button>
                )}
              </CardContent>
            </AddressCard>
          </Grid>
        ))}

        {/* Card para agregar nueva dirección */}
        <Grid size={{ xs: 12, md: 6 }}>
          <AddAddressCard onClick={handleAddNew}>
            <CardContent sx={{ 
              p: 4, 
              textAlign: 'center',
              minHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <AddIcon sx={{ fontSize: 48, color: '#2e5a33', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e5a33', mb: 1 }}>
                Agregar Nueva Dirección
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Añade una nueva dirección para tus envíos
              </Typography>
            </CardContent>
          </AddAddressCard>
        </Grid>
      </Grid>

      {/* Dialog para agregar/editar dirección */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: '900px' },
            borderRadius: { xs: '16px', sm: '20px' },
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          pt: 5,
          px: { xs: 2, sm: 5 },
          background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
          color: 'white',
          borderTopLeftRadius: { xs: '16px', sm: '20px' },
          borderTopRightRadius: { xs: '16px', sm: '20px' },
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            {editingAddress ? 'Editar Dirección' : 'Agregar Nueva Dirección'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre de la dirección *"
                value={formData.apodo}
                onChange={handleChange('apodo')}
                placeholder="Ej: Mi Casa, Oficina"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre de contacto"
                value={formData.nombre_contacto}
                onChange={handleChange('nombre_contacto')}
                placeholder="Nombre de quien recibe"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Calle *"
                value={formData.calle}
                onChange={handleChange('calle')}
                placeholder="Av. Las Condes"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Número *"
                value={formData.numero}
                onChange={(e) => {
                  const value = e.target.value;
                  // Solo permitir números y guiones
                  if (/^[0-9\-]*$/.test(value)) {
                    setFormData(prev => ({ ...prev, numero: value }));
                  }
                }}
                placeholder="1234"
                inputProps={{ maxLength: 10 }}
                helperText="Solo números (ej: 1234 o 123-A)"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Región *"
                value={formData.region}
                onChange={handleChange('region')}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              >
                <option value="">Seleccione una región</option>
                {regiones.map((region) => (
                  <option key={region.id} value={region.nombre}>
                    {region.nombre}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Comuna *"
                value={formData.comuna}
                onChange={handleChange('comuna')}
                SelectProps={{ 
                  native: true,
                  sx: {
                    '& option': {
                      padding: '8px 12px',
                      fontSize: '0.95rem',
                    }
                  }
                }}
                InputLabelProps={{ shrink: true }}
                disabled={!formData.region}
                helperText={!formData.region ? 'Primero selecciona una región' : `${comunasFiltradas.length} comunas disponibles`}
                sx={{
                  ...textFieldStyles,
                  '& select': {
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }
                }}
              >
                <option value="">Seleccione una comuna</option>
                {comunasFiltradas.map((comuna) => (
                  <option key={comuna.id} value={comuna.nombre}>
                    {comuna.nombre}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ciudad"
                value={formData.ciudad}
                onChange={handleChange('ciudad')}
                placeholder="Santiago"
                sx={textFieldStyles}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Teléfono de contacto *"
                value={formData.telefono}
                onChange={handleChange('telefono')}
                onFocus={(e) => {
                  // Si está vacío al hacer foco, poner +56 9
                  if (!e.target.value || e.target.value === '') {
                    setFormData(prev => ({ ...prev, telefono: '+56 9' }));
                  }
                }}
                placeholder="+56 9 1234 5678"
                helperText="Formato automático: +56 9 XXXX XXXX"
                inputProps={{ maxLength: 16 }}
                sx={{
                  ...textFieldStyles,
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Referencias de entrega"
                value={formData.referencia}
                onChange={handleChange('referencia')}
                placeholder="Edificio color beige, portón negro, etc."
                multiline
                rows={2}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2.5, sm: 3.5 }, 
          py: { xs: 2, sm: 2.5 }, 
          backgroundColor: '#fafbfc',
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          borderTop: '1px solid #e8e8e8'
        }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            fullWidth={true}
            sx={{
              borderRadius: { xs: '12px', sm: '12px' },
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.3 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              color: '#555',
              backgroundColor: '#f0f0f0',
              border: '1.5px solid #e0e0e0',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' },
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#e8e8e8',
                borderColor: '#d0d0d0',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            fullWidth={true}
            sx={{
              borderRadius: { xs: '12px', sm: '12px' },
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.3 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' },
              background: 'linear-gradient(135deg, #2e5a33 0%, #3d7547 50%, #4a8c5a 100%)',
              boxShadow: '0 4px 14px rgba(46, 90, 51, 0.35)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3d1e 0%, #2e5a33 50%, #3d7547 100%)',
                boxShadow: '0 6px 20px rgba(46, 90, 51, 0.45)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 8px rgba(46, 90, 51, 0.3)',
              }
            }}
          >
            {editingAddress ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerAddresses;
